import jwtDecode from 'jwt-decode';

const MIDWAY_URL = 'https://midway-auth.amazon.com/SSO';
const TIMEOUT_BUFFER_SECONDS = 30;
const RETRY_ATTEMPTS = 3;
const MIN_REFRESH_INTERVAL_MS = 60_000;
const BASE_RETRY_DELAY_MS = 500;
const ID_TOKEN = 'id_token';
const STATE = 'state';

const token = { value: '' };
const midwayParams: Record<string, string> = {
  scope: 'openid',
  response_type: ID_TOKEN,
  client_id: '',
  redirect_uri: '',
  nonce: '',
};

// De-duplicates concurrent callers into a single in-flight refresh.
let refreshPromise: Promise<void> | null = null;

export async function getMidwayJwtToken(): Promise<string> {
  if (!token.value || isTokenExpired(token.value)) {
    if (!refreshPromise) {
      refreshPromise = refreshToken().finally(() => {
        refreshPromise = null;
      });
    }
    await refreshPromise;
  }
  return token.value;
}

function isTokenExpired(value: string): boolean {
  try {
    const { exp } = jwtDecode<{ exp: number }>(value);
    // Expired once within the refresh buffer of actual expiry.
    return Date.now() >= (exp - TIMEOUT_BUFFER_SECONDS) * 1000;
  } catch {
    return true;
  }
}

async function refreshToken() {
  updateMidwayParams();
  const queryParams = new URLSearchParams(midwayParams).toString();
  const url = `${MIDWAY_URL}?${queryParams}`;
  const response = await fetch(url, { credentials: 'include' });

  if (response.status === 401) {
    window.location.replace(`${MIDWAY_URL}/redirect?${queryParams}`);
    // replace() doesn't halt execution; throw so the 401 body isn't decoded.
    throw new Error('Redirecting to Midway for authentication');
  }
  if (!response.ok) {
    throw new Error(`Midway auth failed with status ${response.status}`);
  }

  removeMidwaySearchParams();
  token.value = await response.text();
  const decodedToken: any = jwtDecode(token.value);
  const expiration = decodedToken.exp;
  // Floor the delay so clock skew / short tokens can't spin a tight loop.
  const delayMs =
    (expiration - Math.round(Date.now() / 1000) - TIMEOUT_BUFFER_SECONDS) * 1000;
  scheduleBackgroundRefresh(Math.max(delayMs, MIN_REFRESH_INTERVAL_MS));
}

function scheduleBackgroundRefresh(delayMs: number) {
  window.setTimeout(() => {
    // On retry exhaustion, reschedule so the loop self-heals when the network recovers.
    retryWithJitter(refreshToken).catch(() =>
      scheduleBackgroundRefresh(MIN_REFRESH_INTERVAL_MS)
    );
  }, delayMs);
}

async function retryWithJitter(
  func: Function,
  attempt: number = 0,
  error: any | unknown = null
) {
  if (attempt >= RETRY_ATTEMPTS) {
    throw error;
  }
  try {
    await func();
  } catch (e: any | unknown) {
    // Jittered exponential backoff so tabs don't retry in lockstep.
    const delay =
      BASE_RETRY_DELAY_MS * Math.pow(2, attempt) + Math.random() * BASE_RETRY_DELAY_MS;
    window.setTimeout(() => retryWithJitter(func, attempt + 1, e), delay);
  }
}

function randomString(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

function updateMidwayParams() {
  // Raw values; URLSearchParams encodes them.
  midwayParams.client_id = window.location.host;
  midwayParams.nonce = randomString();
  midwayParams.redirect_uri = `${window.location}`;
}

function removeMidwaySearchParams() {
  const newLocation = removeLocationSearchParameters(ID_TOKEN, STATE);
  window.history.replaceState({}, '', newLocation);
}

function removeLocationSearchParameters(...keys: string[]): string {
  const location = window.location;
  const queryParams = new URLSearchParams(location.search);
  keys.forEach((key) => queryParams.delete(key));
  const queryString = queryParams.toString();
  const base = location.href.replace(location.search, '');
  const newUrl = `${base}${queryString === '' ? '' : '?'}${queryString}`;
  return newUrl;
}
